import Service from '@/Network/Services/Service'
import request from '@/Network/Requests/request'
import type {
  ListQualificationDocumentalResponse,
  QualificationDocumentalPolicyDetail,
  ListQualificationDocumentalDoctorsResponse,
  QualificationDocumentalDoctor,
  ListQualificationDocumentalPendingResponse,
  ListQualificationDocumentalDomainResponse,
  ListQualificationDocumentalLinksResponse,
  QualificationDocumentalLink,
  ListQualificationDocumentalPolicyLinksResponse,
  ListQualificationDocumentalPolicyOptionsResponse
} from '@/Network/Types/Responses/QualificationDocumentalResponses'
import type { ListAvailableDoctorsResponse } from '@/Network/Types/Responses/QualificationTransferResponses'
import type {
  QualificationDocumentalSearchParams,
  QualificationDocumentalDoctorSearchParams,
  UpdateQualificationDocumentalPolicyRequest,
  ToggleDoctorFieldRequest,
  CreateQualificationDocumentalPolicyRequest,
  ToggleQualificationDocumentalPolicyRequest,
  DuplicateQualificationDocumentalPolicyRequest,
  QualificationDocumentalPendingSearchParams,
  CreateQualificationDocumentalLinkRequest,
  UpdateQualificationDocumentalLinkRequest,
  AddDoctorsToQualificationDocumentalPolicyRequest,
  UpdateQualificationDocumentalPolicyLinksRequest,
  QualificationDocumentalPolicyOptionsSearchParams
} from '@/Network/Types/Requests/QualificationDocumentalRequests'
import type { AvailableDoctorsSearchParams } from '@/Network/Types/Requests/QualificationTransferRequests'

export default class QualificationDocumentalService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/qualification-documental-policy'
  }

  async list(
    params?: QualificationDocumentalSearchParams
  ): Promise<ListQualificationDocumentalResponse> {
    try {
      const { data } = await request.get<ListQualificationDocumentalResponse>(
        this.getCompletePath(),
        params
      )
      return data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar as políticas de qualificação documental.'
      )
    }
  }

  async getById(id: number): Promise<QualificationDocumentalPolicyDetail> {
    try {
      const { data } = await request.get<QualificationDocumentalPolicyDetail>(
        this.getCompletePath(`/${id}`)
      )
      return data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar os detalhes da política de qualificação documental.'
      )
    }
  }

  async create(
    params: CreateQualificationDocumentalPolicyRequest
  ): Promise<QualificationDocumentalPolicyDetail> {
    try {
      const { data } = await request.post<QualificationDocumentalPolicyDetail>(
        this.getCompletePath(),
        params
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível criar a política de qualificação documental.')
    }
  }

  async updatePolicy(
    id: number,
    params: UpdateQualificationDocumentalPolicyRequest
  ): Promise<QualificationDocumentalPolicyDetail> {
    try {
      const { data } = await request.patch(this.getCompletePath(`/${id}`), params)
      return data as QualificationDocumentalPolicyDetail
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível salvar as alterações da política de qualificação documental.'
      )
    }
  }

  async toggleActive(
    id: number,
    params: ToggleQualificationDocumentalPolicyRequest
  ): Promise<QualificationDocumentalPolicyDetail> {
    try {
      const { data } = await request.patch(this.getCompletePath(`/${id}/toggle`), params)
      return data as QualificationDocumentalPolicyDetail
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível alterar a situação da política de qualificação documental.'
      )
    }
  }

  async duplicate(
    id: number,
    params?: DuplicateQualificationDocumentalPolicyRequest
  ): Promise<QualificationDocumentalPolicyDetail> {
    try {
      const { data } = await request.post<QualificationDocumentalPolicyDetail>(
        this.getCompletePath(`/${id}/duplicate`),
        params ?? {}
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível duplicar a política de qualificação documental.')
    }
  }

  async listDoctors(
    policyId: number,
    params?: QualificationDocumentalDoctorSearchParams
  ): Promise<ListQualificationDocumentalDoctorsResponse> {
    try {
      const { data } = await request.get<ListQualificationDocumentalDoctorsResponse>(
        this.getCompletePath(`/${policyId}/doctors`),
        params
      )
      return data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar os médicos da política de qualificação documental.'
      )
    }
  }

  async listDoctorsBySchedule(
    scheduleId: number,
    params?: QualificationDocumentalDoctorSearchParams
  ): Promise<ListQualificationDocumentalDoctorsResponse> {
    try {
      const { data } = await request.get<ListQualificationDocumentalDoctorsResponse>(
        `/schedules/${scheduleId}/qualification-documental-doctors`,
        params
      )
      return data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar os médicos da escala para a política de qualificação documental.'
      )
    }
  }

  async listAvailableDoctors(
    policyId: number,
    params?: AvailableDoctorsSearchParams
  ): Promise<ListAvailableDoctorsResponse> {
    try {
      const { data } = await request.get<ListAvailableDoctorsResponse>(
        this.getCompletePath(`/${policyId}/available-doctors`),
        params
      )
      return data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar os médicos disponíveis para a política de qualificação documental.'
      )
    }
  }

  async toggleDoctorField(
    policyId: number,
    params: ToggleDoctorFieldRequest
  ): Promise<QualificationDocumentalDoctor> {
    try {
      const { doctorId, ...body } = params
      const { data } = await request.patch(
        this.getCompletePath(`/${policyId}/doctors/${doctorId}`),
        body
      )
      return data as QualificationDocumentalDoctor
    } catch (e) {
      throw this.handleError(e, 'Não foi possível atualizar os critérios documentais do médico.')
    }
  }

  async addDoctors(
    policyId: number,
    params: AddDoctorsToQualificationDocumentalPolicyRequest
  ): Promise<void> {
    try {
      await request.post(this.getCompletePath(`/${policyId}/doctors`), params)
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível adicionar os médicos à política de qualificação documental.'
      )
    }
  }

  async removeDoctor(policyId: number, doctorId: number): Promise<void> {
    try {
      await request.delete(this.getCompletePath(`/${policyId}/doctors/${doctorId}`))
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível remover o médico da política de qualificação documental.'
      )
    }
  }

  async listPendingEvaluations(
    policyId: number,
    params?: QualificationDocumentalPendingSearchParams
  ): Promise<ListQualificationDocumentalPendingResponse> {
    try {
      const { data } = await request.get<ListQualificationDocumentalPendingResponse>(
        this.getCompletePath(`/${policyId}/pending-evaluations`),
        params
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as avaliações documentais pendentes.')
    }
  }

  async listCompanyDomain(policyId: number): Promise<ListQualificationDocumentalDomainResponse> {
    try {
      const { data } = await request.get<ListQualificationDocumentalDomainResponse>(
        this.getCompletePath(`/${policyId}/domain/companies`)
      )
      return data
    } catch (e) {
      // fallback intencional: os domínios alimentam apenas os chips de abrangência
      // (leitura auxiliar). Uma falha aqui não deve derrubar o carregamento da
      // política — a tela renderiza a abrangência vazia em vez de quebrar.
      return { data: [], count: 0 }
    }
  }

  async listHealthUnitDomain(policyId: number): Promise<ListQualificationDocumentalDomainResponse> {
    try {
      const { data } = await request.get<ListQualificationDocumentalDomainResponse>(
        this.getCompletePath(`/${policyId}/domain/health-units`)
      )
      return data
    } catch (e) {
      // fallback intencional: os domínios alimentam apenas os chips de abrangência
      // (leitura auxiliar). Uma falha aqui não deve derrubar o carregamento da
      // política — a tela renderiza a abrangência vazia em vez de quebrar.
      return { data: [], count: 0 }
    }
  }

  async listScheduleDomain(policyId: number): Promise<ListQualificationDocumentalDomainResponse> {
    try {
      const { data } = await request.get<ListQualificationDocumentalDomainResponse>(
        this.getCompletePath(`/${policyId}/domain/schedules`)
      )
      return data
    } catch (e) {
      // fallback intencional: os domínios alimentam apenas os chips de abrangência
      // (leitura auxiliar). Uma falha aqui não deve derrubar o carregamento da
      // política — a tela renderiza a abrangência vazia em vez de quebrar.
      return { data: [], count: 0 }
    }
  }

  async listPositionDomain(policyId: number): Promise<ListQualificationDocumentalDomainResponse> {
    try {
      const { data } = await request.get<ListQualificationDocumentalDomainResponse>(
        this.getCompletePath(`/${policyId}/domain/positions`)
      )
      return data
    } catch (e) {
      // fallback intencional: os domínios alimentam apenas os chips de abrangência
      // (leitura auxiliar). Uma falha aqui não deve derrubar o carregamento da
      // política — a tela renderiza a abrangência vazia em vez de quebrar.
      return { data: [], count: 0 }
    }
  }

  async listScheduleLinks(scheduleId: number): Promise<ListQualificationDocumentalLinksResponse> {
    try {
      const { data } = await request.get<ListQualificationDocumentalLinksResponse>(
        `/schedules/${scheduleId}/qualification-documental-links`
      )
      return data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar os vínculos de política de qualificação documental da escala.'
      )
    }
  }

  async createScheduleLink(
    scheduleId: number,
    params: CreateQualificationDocumentalLinkRequest
  ): Promise<QualificationDocumentalLink> {
    try {
      const { data } = await request.post<QualificationDocumentalLink>(
        `/schedules/${scheduleId}/qualification-documental-links`,
        params
      )
      return data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível vincular a política de qualificação documental à escala.'
      )
    }
  }

  async updateScheduleLink(
    scheduleId: number,
    linkId: number,
    params: UpdateQualificationDocumentalLinkRequest
  ): Promise<QualificationDocumentalLink> {
    try {
      const { data } = await request.put<QualificationDocumentalLink>(
        `/schedules/${scheduleId}/qualification-documental-links/${linkId}`,
        params
      )
      return data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível editar o vínculo de política de qualificação documental da escala.'
      )
    }
  }

  async deleteScheduleLink(scheduleId: number, linkId: number): Promise<void> {
    try {
      await request.delete(`/schedules/${scheduleId}/qualification-documental-links/${linkId}`)
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível excluir o vínculo de política de qualificação documental da escala.'
      )
    }
  }

  // Vínculos de política documental por recurso (ResourcePoliciesModal).
  // O handleError preserva a message pt-BR do backend (validações de vigência do
  // PATCH: sobreposição, gap, vigência aberta etc.) dentro do ServiceError, que o
  // PolicyLinksManager exibe no toast via getApiErrorMessage.

  async listCompanyPolicyLinks(
    companyId: number
  ): Promise<ListQualificationDocumentalPolicyLinksResponse> {
    try {
      const { data } = await request.get<ListQualificationDocumentalPolicyLinksResponse>(
        this.getCompletePath(`/company/${companyId}`)
      )
      return data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar os vínculos de política de qualificação documental da empresa.'
      )
    }
  }

  async listHealthUnitPolicyLinks(
    healthUnitId: number
  ): Promise<ListQualificationDocumentalPolicyLinksResponse> {
    try {
      const { data } = await request.get<ListQualificationDocumentalPolicyLinksResponse>(
        this.getCompletePath(`/health-unit/${healthUnitId}`)
      )
      return data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar os vínculos de política de qualificação documental da unidade.'
      )
    }
  }

  async listPositionPolicyLinks(
    positionId: number
  ): Promise<ListQualificationDocumentalPolicyLinksResponse> {
    try {
      const { data } = await request.get<ListQualificationDocumentalPolicyLinksResponse>(
        this.getCompletePath(`/position/${positionId}`)
      )
      return data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar os vínculos de política de qualificação documental do posto.'
      )
    }
  }

  async updateCompanyPolicyLinks(
    companyId: number,
    payload: UpdateQualificationDocumentalPolicyLinksRequest
  ): Promise<void> {
    try {
      await request.patch(this.getCompletePath(`/company/${companyId}/policy-links`), payload)
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível salvar os vínculos de política de qualificação documental da empresa.'
      )
    }
  }

  async updateHealthUnitPolicyLinks(
    healthUnitId: number,
    payload: UpdateQualificationDocumentalPolicyLinksRequest
  ): Promise<void> {
    try {
      await request.patch(
        this.getCompletePath(`/health-unit/${healthUnitId}/policy-links`),
        payload
      )
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível salvar os vínculos de política de qualificação documental da unidade.'
      )
    }
  }

  async updatePositionPolicyLinks(
    positionId: number,
    payload: UpdateQualificationDocumentalPolicyLinksRequest
  ): Promise<void> {
    try {
      await request.patch(this.getCompletePath(`/position/${positionId}/policy-links`), payload)
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível salvar os vínculos de política de qualificação documental do posto.'
      )
    }
  }

  async listPolicyOptions(
    params?: QualificationDocumentalPolicyOptionsSearchParams
  ): Promise<ListQualificationDocumentalPolicyOptionsResponse> {
    const response = await this.list({
      q: params?.q,
      companyIds: params?.companyId ? [params.companyId] : undefined,
      offset: params?.offset,
      limit: params?.limit
    })
    return {
      data: response.data.map((policy) => ({
        id: policy.id,
        name: policy.policyName,
        company: { name: policy.company }
      })),
      count: response.count
    }
  }
}
